// End-of-call sheet (Phase 3): feedback + save-reflection, shown when the
// session ends — replaces the old auto-redirect to landing.

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BookmarkCheck,
  Bookmark,
  Home,
  Loader2,
  ThumbsDown,
  ThumbsUp,
} from "lucide-react";

import { getDeviceId } from "@/lib/device";
import { track } from "@/lib/analytics";
import { isSignedIn, setAuth, maskPhone } from "@/lib/auth";
import PhoneLinkCard from "@/components/PhoneLinkCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RATINGS = [
  { id: "up", label: "Helped", Icon: ThumbsUp },
  { id: "down", label: "Didn't help", Icon: ThumbsDown },
  { id: "unsafe", label: "Felt unsafe", Icon: null },
  { id: "faith_mismatch", label: "Didn't match my faith", Icon: null },
];

export default function EndOfCallSheet({ open, sessionId, transcript }) {
  const navigate = useNavigate();

  const [rating, setRating] = useState(null);
  const [comment, setComment] = useState("");
  const [feedbackState, setFeedbackState] = useState("idle"); // idle|sending|sent

  const [includeTranscript, setIncludeTranscript] = useState(false);
  const [saveState, setSaveState] = useState("idle"); // idle|saving|saved|error
  const [savedTitle, setSavedTitle] = useState("");
  const [signedIn, setSignedIn] = useState(() => isSignedIn());
  const [linkedPhone, setLinkedPhone] = useState("");

  if (!open) return null;

  const lines = (transcript || []).filter(
    (m) => m.text && m.text !== "\u2026",
  );
  const canSave = lines.length > 0;

  const sendFeedback = async () => {
    if (!rating && !comment.trim()) return;
    setFeedbackState("sending");
    try {
      await fetch(`${API}/feedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: sessionId || null,
          device_id: getDeviceId(),
          rating: rating || null,
          comment: comment.trim() || null,
        }),
      });
      track("feedback_given", { sessionId, props: { rating: rating || "comment_only" } });
      setFeedbackState("sent");
    } catch {
      setFeedbackState("sent"); // never trap the user in a failed state here
    }
  };

  const saveReflection = async () => {
    if (!canSave || saveState === "saving" || saveState === "saved") return;
    setSaveState("saving");
    try {
      const res = await fetch(`${API}/reflections`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          device_id: getDeviceId(),
          session_id: sessionId || null,
          transcript: lines.map((m) => ({ who: m.who, text: m.text })),
          save_transcript: includeTranscript,
        }),
      });
      if (!res.ok) throw new Error(`http_${res.status}`);
      const data = await res.json();
      setSavedTitle(data.title || "");
      track("reflection_saved", {
        sessionId,
        props: { transcript_opt_in: includeTranscript },
      });
      setSaveState("saved");
    } catch {
      setSaveState("error");
    }
  };

  return (
    <div
      data-testid="end-of-call-sheet"
      className="fixed inset-0 z-[80] flex items-end justify-center sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-label="Call ended"
    >
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background: "hsla(40, 30%, 20%, 0.35)",
          backdropFilter: "blur(10px)",
          WebkitBackdropFilter: "blur(10px)",
        }}
      />

      <div className="relative z-10 max-h-[90vh] w-full max-w-md overflow-y-auto cg-card cg-fade-up rounded-t-[2rem] p-7 sm:rounded-[2rem]">
        <p className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
          The call has ended
        </p>
        <h2
          className="font-serif-display mt-1 text-foreground"
          style={{ fontSize: "1.8rem", lineHeight: 1.25 }}
        >
          Go in peace.
        </h2>
        <p className="mt-1.5 text-sm leading-6 text-muted-foreground">
          Carry one small light into the next hour.
        </p>

        {/* ---- feedback ---- */}
        <div className="mt-6">
          {feedbackState === "sent" ? (
            <p
              data-testid="feedback-thanks"
              className="rounded-xl px-4 py-3 text-sm text-foreground/85"
              style={{ background: "hsla(43, 50%, 90%, 0.7)" }}
            >
              Thank you. Your words help this voice grow gentler.
            </p>
          ) : (
            <>
              <p className="text-sm font-medium text-foreground">
                How did this feel?
              </p>
              <div className="mt-2.5 flex flex-wrap gap-2">
                {RATINGS.map(({ id, label, Icon }) => (
                  <button
                    key={id}
                    type="button"
                    data-testid={`feedback-${id}`}
                    onClick={() => setRating((r) => (r === id ? null : id))}
                    className="inline-flex min-h-[40px] items-center gap-1.5 rounded-full border px-3.5 text-[13px] transition-colors"
                    style={
                      rating === id
                        ? {
                            background: "hsl(30 12% 12%)",
                            color: "hsl(43 45% 96%)",
                            borderColor: "hsl(30 12% 12%)",
                          }
                        : {
                            borderColor: "hsla(38, 25%, 70%, 0.7)",
                            color: "hsl(30 10% 32%)",
                          }
                    }
                  >
                    {Icon ? <Icon className="h-3.5 w-3.5" strokeWidth={1.8} /> : null}
                    {label}
                  </button>
                ))}
              </div>
              <textarea
                data-testid="feedback-comment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Anything you want to add… (optional)"
                rows={2}
                className="mt-3 w-full resize-none rounded-xl border bg-white/70 px-3.5 py-2.5 text-sm text-foreground outline-none placeholder:text-muted-foreground/70"
                style={{ borderColor: "hsla(38, 25%, 75%, 0.8)" }}
              />
              <button
                type="button"
                data-testid="feedback-submit"
                onClick={sendFeedback}
                disabled={(!rating && !comment.trim()) || feedbackState === "sending"}
                className="cg-btn-ghost mt-2 !min-h-[42px] !px-5 text-sm disabled:cursor-not-allowed disabled:opacity-40"
              >
                {feedbackState === "sending" ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  "Send feedback"
                )}
              </button>
            </>
          )}
        </div>

        {/* ---- save reflection ---- */}
        {canSave ? (
          <div
            className="mt-6 border-t pt-5"
            style={{ borderColor: "hsla(38, 25%, 80%, 0.6)" }}
          >
            {saveState === "saved" ? (
              <div data-testid="reflection-saved">
                <p className="flex items-center gap-2 text-sm font-medium text-foreground">
                  <BookmarkCheck
                    className="h-4 w-4"
                    style={{ color: "hsl(42 50% 45%)" }}
                    strokeWidth={1.8}
                  />
                  Saved{savedTitle ? ` — “${savedTitle}”` : ""}
                </p>
                <button
                  type="button"
                  data-testid="view-history-link"
                  onClick={() => navigate("/history")}
                  className="mt-2 text-sm underline underline-offset-4 text-muted-foreground transition-colors hover:text-foreground"
                >
                  View it in your history
                </button>
              </div>
            ) : (
              <>
                <p className="text-sm font-medium text-foreground">
                  Keep this moment?
                </p>
                <p className="mt-1 text-[13px] leading-5 text-muted-foreground">
                  A short, gentle summary is written for you and kept only on
                  this device.
                </p>
                <label className="mt-3 flex cursor-pointer items-start gap-2.5 text-[13px] leading-5 text-foreground/85">
                  <input
                    type="checkbox"
                    data-testid="transcript-opt-in"
                    checked={includeTranscript}
                    onChange={(e) => setIncludeTranscript(e.target.checked)}
                    className="mt-0.5 h-4 w-4 accent-[#1A1815]"
                  />
                  Also keep the full transcript (off by default)
                </label>
                {saveState === "error" ? (
                  <p className="mt-2 text-[13px]" style={{ color: "hsl(0 60% 42%)" }}>
                    Could not save right now. Try once more.
                  </p>
                ) : null}
                <button
                  type="button"
                  data-testid="save-reflection-btn"
                  onClick={saveReflection}
                  disabled={saveState === "saving"}
                  className="cg-btn-primary mt-3 !min-h-[44px] !px-5 text-sm disabled:opacity-60"
                >
                  {saveState === "saving" ? (
                    <span className="inline-flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Writing your reflection…
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-2">
                      <Bookmark className="h-4 w-4" strokeWidth={1.8} />
                      Save this reflection
                    </span>
                  )}
                </button>
              </>
            )}
          </div>
        ) : null}

        {/* ---- account: save history via phone number ---- */}
        <div
          className="mt-6 border-t pt-5"
          style={{ borderColor: "hsla(38, 25%, 80%, 0.6)" }}
        >
          {signedIn ? (
            <p
              data-testid="account-linked-note"
              className="rounded-xl px-4 py-3 text-sm text-foreground/85"
              style={{ background: "hsla(43, 50%, 90%, 0.7)" }}
            >
              {linkedPhone
                ? `Account created — your conversations are saved to ${maskPhone(linkedPhone)}.`
                : "Signed in — your conversations are saved to your account."}
            </p>
          ) : (
            <PhoneLinkCard
              compact
              title="Keep your history"
              subtitle="Enter your phone number and we'll text you a code — your saved conversations will be waiting whenever you return, on any device."
              onLinked={(phone) => {
                setAuth(phone);
                setLinkedPhone(phone);
                setSignedIn(true);
              }}
            />
          )}
        </div>

        {/* ---- return home ---- */}
        <div
          className="mt-6 border-t pt-5"
          style={{ borderColor: "hsla(38, 25%, 80%, 0.6)" }}
        >
          <button
            type="button"
            data-testid="return-home-btn"
            onClick={() => navigate("/")}
            className="cg-btn-ghost w-full !min-h-[46px] text-sm"
          >
            <span className="inline-flex items-center gap-2">
              <Home className="h-4 w-4" strokeWidth={1.8} />
              Return home
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}
